import styled from "styled-components";
import Main from "src/components/core/Main";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchInput from "src/components/purchase/SearchInput";
import FilterMobileButton from "src/components/purchase/FilterMobileButton";
import DateSelected from "src/components/purchase/DateSelected";
import SellerSelected from "src/components/purchase/SellerSelected";
import StatusBar from "src/components/purchase/StatusBar";
import FailedReasonSelected from "src/components/purchase/FailedReasonSelected";
import NoResultsOverlay from "../NoResultsOverlay";
import NoRowsOverlay from "../NoRowsOverlay";
import { getLocale } from "react-i18nify";
import { GridActionsCellItem } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { convertIntToCurrency } from "src/common";
import useFilterOrder from "../hooks/useFilterOrder";
import { getRefundOrderList } from "src/api/cancelOrder";
import FilterDialog from "src/components/purchase/FilterDialog";
import { useDialog } from "src/context/DialogContext";

const Container = styled.div`
  width: 100%;
  height: auto;
  min-height: 850px;
  padding: 24px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 12px;
  }

  @media (max-width: 375px) {
    padding: 8px;
  }
`;

const WrapperFilter = styled.div`
  padding-top: 10px;
  margin-bottom: 20px;
  display: flex;
  gap: 16px;
  overflow-x: scroll;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const WrapperMobileButton = styled.div`
  display: ${({ isSmallerThanTablet }) =>
    isSmallerThanTablet ? "flex" : "none"};
  justify-content: space-between;
  gap: 8px;
`;

const WrapperDataGrid = styled.div`
  height: 630px;
  width: 100%;
  .MuiDataGrid-root {
    border: 1px solid #ffffff;
  }
  .MuiDataGrid-columnHeaders {
    background-color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    // outline: solid #f5f5f5 1px;
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
`;

const CancelRefundList = ({ open }) => {
  const { openDialog, closeDialog } = useDialog();
  const isSmallerThanTablet = useMediaQuery("(max-width:767px)");

  const config = {
    module: "refund",
    initialState: {
      keyword: "",
      page: 0,
      page_size: 10,
      status: "all",
      date: null,
      seller: "",
      failedReason: "",
    },
    initialVisibilityModel: {
      orderNumber: true,
      customerName: true,
      sellerName: !isSmallerThanTablet,
      price: !isSmallerThanTablet,
      cancelReason: !isSmallerThanTablet,
      status: true,
      actions: true,
    },
    onFetch: getRefundOrderList,
    isSmallerThanTablet,
    dependencies: [
      "date",
      "failedReason",
      "seller",
      "page",
      "seller",
      "status",
      "sortBy",
      "sortOrder",
    ],
  };

  const {
    dataList,
    onSearchButton,
    handlePageChange,
    rowCount,
    statusCount,
    dataSearch,
    setDataSearch,
    isLoading,
    columnVisibilityModel,
    handleFilterDialog,
  } = useFilterOrder(config);

  const statusTabs = [
    { label: "ทั้งหมด", count: statusCount?.all || 0, color: "", value: "all" },
    {
      label: "คืนเงิน",
      count: statusCount?.waitingRefund || 0,
      color: "#BF0013",
      value: "waiting refund",
    },
    {
      label: "คืนเงินสำเร็จ",
      count: statusCount?.refunded || 0,
      color: "#219653",
      value: "refunded",
    },
    {
      label: "ยกเลิก",
      count: statusCount?.cancel || 0,
      color: "#BCBCC0",
      value: "cancel",
    },
  ];

  const columns = [
    {
      field: "orderNumber",
      type: "number",
      flex: 1,
      minWidth: isSmallerThanTablet ? 92 : 160,
      editable: false,
      sortable: true,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
            }}
          >
            {isSmallerThanTablet ? "Order No." : "Order Number"}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: "#6C6C70",
              fontSize: isSmallerThanTablet ? "0.625rem" : "0.75rem",
            }}
          >
            {isSmallerThanTablet ? "QT No." : "QT Number"}
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <div style={{ overflow: "hidden" }}>
            <div
              className="rowitem"
              style={{
                fontWeight: 400,
                color: "#252525 ",
                fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params?.row?.order?.orderNumber}
            </div>
            <div
              className="rowitem"
              style={{
                fontWeight: 400,
                color: "#6C6C70",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params?.row?.order?.qtNumber}
            </div>
          </div>
        );
      },
    },
    {
      field: "customerName",
      flex: 1,
      minWidth: isSmallerThanTablet ? 131 : 160,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
            }}
          >
            ชื่อลูกค้า
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: "#6C6C70",
              fontSize: isSmallerThanTablet ? "0.625rem" : "0.75rem",
            }}
          >
            {isSmallerThanTablet ? "ร้านค้า" : "เบอร์ติดต่อ"}
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "start",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "24px 0px",
            }}
          >
            <div
              style={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <div
                className="MuiDataGrid-cellContent"
                style={{
                  fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params.row?.order?.shippingAddress?.name
                  ? `${params.row?.order?.shippingAddress?.name}`
                  : `-`}
              </div>
              {isSmallerThanTablet ? (
                <div
                  className="MuiDataGrid-cellContent"
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#6C6C70",
                  }}
                >
                  {params.row?.order?.sellerName
                    ? `${params.row?.order?.sellerName}`
                    : `-`}
                </div>
              ) : (
                <div
                  className="MuiDataGrid-cellContent"
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "#6C6C70",
                  }}
                >
                  {params.row?.order?.shippingAddress?.tel
                    ? `${params.row?.order?.shippingAddress?.tel}`
                    : `-`}
                </div>
              )}
            </div>
          </Box>
        );
      },
    },
    {
      field: "sellerName",
      flex: 2,
      minWidth: 250,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            ร้านค้า
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#6C6C70" }}
          >
            สินค้า
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              fontSize="0.875rem"
              className="rowitem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {params?.row?.order?.sellerName
                ? params?.row?.order?.sellerName
                : "-"}
            </Typography>
            {params?.row?.order?.productChoiceOrders?.map(
              ({ productChoiceOrder, color }, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: "8px",
                    color: "#6C6C70",
                    paddingLeft: "4px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Box style={{ fontSize: "0.75rem", fontWeight: "400" }}>
                    •
                  </Box>
                  <Box
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "400",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`${productChoiceOrder?.product?.item_name} ${color?.colorEn ? color?.colorEn : ""}`}
                  </Box>
                </Box>
              ),
            )}
          </Stack>
        );
      },
    },
    {
      field: "price",
      flex: 1,
      minWidth: 85,
      editable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "ราคา" : "price",
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {convertIntToCurrency(params?.row?.order?.totalAll)}
          </div>
        );
      },
    },
    {
      field: "cancelReason",
      flex: 1,
      minWidth: 120,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "สาเหตุที่ยกเลิก" : "cancel reason",
      renderCell: (params) => {
        return (
          <div className="rowitem">{params?.row?.status?.cancelReason}</div>
        );
      },
    },
    {
      field: "status",
      headerClassName: "status-header",
      flex: 1,
      minWidth: isSmallerThanTablet ? 48 : 120,
      editable: false,
      sortable: false,
      headerAlign: isSmallerThanTablet ? "center" : "left",
      align: isSmallerThanTablet ? "center" : "left",
      flexDirection: "column",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "สถานะ" : "Status",
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              margin: "8px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: isSmallerThanTablet ? "center" : "start",
              outline: "none",
              whiteSpace: "nowrap",
            }}
          >
            <Stack
              direction="row"
              alignItems={"center"}
              justifyContent={"start"}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: statusTabs.find(
                    (tab) => tab.value === params.row.status.value,
                  )?.color,
                  marginRight: 1,
                }}
              />

              {!isSmallerThanTablet && (
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    maxWidth: "100px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {
                    statusTabs.find(
                      (tab) => tab.value === params.row.status.value,
                    )?.label
                  }
                </Typography>
              )}
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "ดำเนินการ",
      type: "actions",
      flex: 1,
      minWidth: isSmallerThanTablet ? 40 : 80,
      sortable: false,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="รายละเอียด"
          onClick={() =>
            window.open(`/cancel/detail/${params.row._id}`, "_blank")
          }
          showInMenu
        />,
      ],
    },
  ];

  const handleColumnClick = ({ field }) => {
    if (field === "orderNumber") {
      setDataSearch((prev) => ({
        ...prev,
        sortBy: "order.orderNumber",
        sortOrder: dataSearch.sortOrder === "asc" ? "desc" : "asc",
        page: 0,
      }));
    }
  };

  const handleOpenDialog = () => {
    openDialog({
      type: "custom",
      customDialog: (
        <FilterDialog
          dataSearch={dataSearch}
          onClose={() => closeDialog()}
          onConfirm={handleFilterDialog}
          dateLabel="วันที่สั่งซื้อ"
          isFilterSeller={true}
          isFailedReason={true}
        />
      ),
    });
  };

  return (
    <Main open={open}>
      <Container>
        <WrapperFilter>
          <SearchInput
            onSearch={onSearchButton}
            setDataSearch={setDataSearch}
          />

          <WrapperMobileButton isSmallerThanTablet={isSmallerThanTablet}>
            <FilterMobileButton
              onDialog={handleOpenDialog}
              dataSearch={dataSearch}
            />
          </WrapperMobileButton>

          <DateSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dateLabel={"วันที่สั่งซื้อ"}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />

          <SellerSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />

          <FailedReasonSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />
        </WrapperFilter>

        <StatusBar
          dataSearch={dataSearch}
          setDataSearch={setDataSearch}
          statusTabs={statusTabs}
        />

        <WrapperDataGrid>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowX: "scroll",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <DataGrid
              rows={dataList}
              getRowId={(row) => row._id}
              getRowHeight={() => "auto"}
              loading={isLoading}
              columns={columns}
              pageSize={10}
              rowCount={rowCount}
              paginationMode="server"
              page={dataSearch.page}
              onPageChange={(e) => handlePageChange(e)}
              rowsPerPageOptions={[10]}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay,
                NoResultsOverlay,
              }}
              componentsProps={{
                basePopper: {},
              }}
              sx={{
                "& .MuiDataGrid-actionsCell": {
                  transform: "rotate(90deg)",
                },
                "&. MuiDataGrid-columnHeaderTitleContainerContent": {
                  lineHeight: "normal",
                },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                },
                "& .status-header": {
                  padding: 0,
                },
              }}
              disableColumnResize={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnHeaderClick={handleColumnClick}
            />
          </Box>
        </WrapperDataGrid>
      </Container>
    </Main>
  );
};

export default CancelRefundList;
